import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, HStack, ScrollView, VStack, useTheme } from 'native-base';
import MaterialCommunityIcons from 'react-native-vector-icons/MaterialCommunityIcons';
import { first } from 'lodash';

import { Button, Text } from '@pimm/base';
import { SiteConfigDto } from '@pimm/services/lib/sms-tenants';
import { KitchenLayoutDto } from '@pimm/services/lib/sms-positioning';
import { PositionGroupDto, SmsWorkforceApi } from '@pimm/services/lib/sms-workforce';
import { formatToISOString } from '@app/utils/date-formatter';
import { useDayBlockFocus } from '@app/hooks/day-blocks.hook';

type StoreConfigProps = {
  kitchenLayout?: KitchenLayoutDto;
  siteConfig?: SiteConfigDto;
  onCancel?: () => void;
};

export const StoreConfig = ({ kitchenLayout, siteConfig, ...props }: StoreConfigProps) => {
  const { t } = useTranslation();
  const { colors } = useTheme();
  const dayBlockFocus = useDayBlockFocus();
  const [positionGroup, setPositionGroup] = useState<PositionGroupDto>();

  useEffect(() => {
    if (siteConfig && siteConfig.id) {
      const dayBlock = dayBlockFocus.selectNow();
      const dateTime = formatToISOString(dayBlock.startTime).split('T');

      SmsWorkforceApi.GetPositionSchedules({
        siteId: siteConfig.id,
        date: dateTime[0],
        groupStartTime: dateTime[1],
      }).then(result => {
        setPositionGroup(first(result));
      });
    }
  }, [siteConfig?.id]);

  return (
    <VStack>
      <HStack alignItems="center" px={4} h="52px" borderBottomWidth={1}>
        <Text size="xl" fontWeight={600} color="gray.900">
          {t('store_management:store_configuration')}
        </Text>
      </HStack>

      <Box flex={1}>
        <ScrollView bg="gray.50">
          <VStack space={4} py={3} px={4}>
            {/* Corporate Information */}
            <Box>
              <Text size="lg" fontWeight={700} mb={1} color="gray.900">
                {t('store_management:corporate_information')}
              </Text>

              <VStack rounded="lg" space={3} p={4} borderWidth={1} bg="white">
                <HStack space={2}>
                  <VStack space={1} w="1/3">
                    <Text size="md" fontWeight={500} color="gray.600" numberOfLines={1} ellipsizeMode="tail" lineHeight="xs">
                      {t('store_management:brand')}
                    </Text>
                    <Text size="md" fontWeight={600} color="black" numberOfLines={1} ellipsizeMode="tail" lineHeight="xs">
                      {siteConfig?.companyInfo?.brand?.name ?? '--'}
                    </Text>
                  </VStack>
                  <VStack space={1} w="1/3">
                    <Text size="md" fontWeight={500} color="gray.600" numberOfLines={1} ellipsizeMode="tail" lineHeight="xs">
                      {t('store_management:market')}
                    </Text>
                    <Text size="md" fontWeight={600} color="black" numberOfLines={1} ellipsizeMode="tail" lineHeight="xs">
                      {siteConfig?.configSettings?.marketTitle}
                    </Text>
                  </VStack>
                </HStack>

                <HStack space={2}>
                  <VStack space={1} w="1/3">
                    <Text size="md" fontWeight={500} color="gray.600" numberOfLines={1} ellipsizeMode="tail" lineHeight="xs">
                      {t('store_management:customer_name')}
                    </Text>
                    <Text size="md" fontWeight={600} color="black" numberOfLines={1} ellipsizeMode="tail" lineHeight="xs">
                      {siteConfig?.companyInfo?.consolidationName ?? '--'}
                    </Text>
                  </VStack>
                  <VStack space={1} w="1/3">
                    <Text size="md" fontWeight={500} color="gray.600" numberOfLines={1} ellipsizeMode="tail" lineHeight="xs">
                      {t('store_management:customer_code')}
                    </Text>
                    <Text size="md" fontWeight={600} color="black" numberOfLines={1} ellipsizeMode="tail" lineHeight="xs">
                      {siteConfig?.companyInfo?.chainName ?? '--'}
                    </Text>
                  </VStack>
                </HStack>
              </VStack>
            </Box>

            {/* Site Configuration */}
            <Box>
              <Text size="lg" fontWeight={700} mb={1} color="gray.900">
                {t('store_management:site_configuration')}
              </Text>

              <VStack rounded="lg" space={3} p={4} borderWidth={1} bg="white">
                <HStack space={2}>
                  <VStack space={1} w="1/3">
                    <Text size="md" fontWeight={500} color="gray.600" numberOfLines={1} ellipsizeMode="tail" lineHeight="xs">
                      {t('store_management:ops_mode')}
                    </Text>
                    <Text size="md" fontWeight={600} color="black" numberOfLines={1} ellipsizeMode="tail" lineHeight="xs">
                      {siteConfig?.configSettings?.opsModeTitle ?? '--'}
                    </Text>
                  </VStack>
                  <VStack space={1} w="1/3">
                    <Text size="md" fontWeight={500} color="gray.600" numberOfLines={1} ellipsizeMode="tail" lineHeight="xs">
                      {t('store_management:sales_volume_profile')}
                    </Text>
                    <Text size="md" fontWeight={600} color="black" numberOfLines={1} ellipsizeMode="tail" lineHeight="xs">
                      {siteConfig?.configSettings?.salesVolumeProfile ?? '--'}
                    </Text>
                  </VStack>
                  <VStack space={1} w="1/3">
                    <Text size="md" fontWeight={500} color="gray.600" numberOfLines={1} ellipsizeMode="tail" lineHeight="xs">
                      {t('store_management:sms_plan')}
                    </Text>
                    <Text size="md" fontWeight={600} color="black" numberOfLines={1} ellipsizeMode="tail" lineHeight="xs">
                      {siteConfig?.config?.smsPlan ?? '--'}
                    </Text>
                  </VStack>
                </HStack>
              </VStack>
            </Box>

            {/* Kitchen Information */}
            <Box>
              <Text size="lg" fontWeight={700} mb={1} color="gray.900">
                {t('store_management:kitchen_information')}
              </Text>

              <VStack rounded="lg" space={3} p={4} borderWidth={1} bg="white">
                <HStack space={2}>
                  <VStack space={1} w="1/3">
                    <Text size="md" fontWeight={500} color="gray.600" numberOfLines={1} ellipsizeMode="tail" lineHeight="xs">
                      {t('store_management:type')}
                    </Text>
                    <Text size="md" fontWeight={600} color="black" numberOfLines={1} ellipsizeMode="tail" lineHeight="xs">
                      {kitchenLayout?.kitchenType?.title ?? '--'}
                    </Text>
                  </VStack>
                  <VStack space={1} w="1/3">
                    <Text size="md" fontWeight={500} color="gray.600" numberOfLines={1} ellipsizeMode="tail" lineHeight="xs">
                      {t('store_management:code')}
                    </Text>
                    <Text size="md" fontWeight={600} color="black" numberOfLines={1} ellipsizeMode="tail" lineHeight="xs">
                      {kitchenLayout?.code ?? '--'}
                    </Text>
                  </VStack>
                </HStack>

                <HStack space={2}>
                  <VStack space={1} w="1/3">
                    <Text size="md" fontWeight={500} color="gray.600" numberOfLines={1} ellipsizeMode="tail" lineHeight="xs">
                      {t('store_management:puw_count')}
                    </Text>
                    <Text size="md" fontWeight={600} color="black" numberOfLines={1} ellipsizeMode="tail" lineHeight="xs">
                      {kitchenLayout?.puwCount ?? 0}
                    </Text>
                  </VStack>
                  <VStack space={1} w="1/3">
                    <Text size="md" fontWeight={500} color="gray.600" numberOfLines={1} ellipsizeMode="tail" lineHeight="xs">
                      {t('store_management:sl_count')}
                    </Text>
                    <Text size="md" fontWeight={600} color="black" numberOfLines={1} ellipsizeMode="tail" lineHeight="xs">
                      {kitchenLayout?.slCount ?? 0}
                    </Text>
                  </VStack>
                </HStack>

                <HStack space={2}>
                  <VStack space={1} w="1/3">
                    <Text size="md" fontWeight={500} color="gray.600" numberOfLines={1} ellipsizeMode="tail" lineHeight="xs">
                      {t('store_management:title')}
                    </Text>
                    <Text size="md" fontWeight={600} color="black" numberOfLines={1} ellipsizeMode="tail" lineHeight="xs">
                      {kitchenLayout?.title ?? '--'}
                    </Text>
                  </VStack>
                </HStack>
              </VStack>
            </Box>

            {/* Position Plan (Current) */}
            <Box>
              <Text size="lg" fontWeight={700} mb={1} color="gray.900">
                {t('store_management:position_plan_current')}
              </Text>

              <VStack rounded="lg" space={3} p={4} borderWidth={1} bg="white">
                <HStack space={2}>
                  <VStack space={1} w="1/3">
                    <Text size="md" fontWeight={500} color="gray.600" numberOfLines={1} ellipsizeMode="tail" lineHeight="xs">
                      {t('store_management:code')}
                    </Text>
                    <Text size="md" fontWeight={600} color="black" numberOfLines={1} ellipsizeMode="tail" lineHeight="xs">
                      {positionGroup?.positionMap?.code ?? '--'}
                    </Text>
                  </VStack>
                </HStack>

                <HStack space={2}>
                  <VStack space={1}>
                    <Text size="md" fontWeight={500} color="gray.600" numberOfLines={1} ellipsizeMode="tail" lineHeight="xs">
                      {t('store_management:title')}
                    </Text>
                    <Text size="md" fontWeight={600} color="black" numberOfLines={1} ellipsizeMode="tail" lineHeight="xs">
                      {positionGroup?.positionMap?.title ?? '--'}
                    </Text>
                  </VStack>
                </HStack>

                <HStack space={2}>
                  <VStack space={1} w="1/3">
                    <Text size="md" fontWeight={500} color="gray.600" numberOfLines={1} ellipsizeMode="tail" lineHeight="xs">
                      {t('store_management:start_time')}
                    </Text>
                    <Text size="md" fontWeight={600} color="black" numberOfLines={1} ellipsizeMode="tail" lineHeight="xs">
                      {positionGroup?.positionMap?.daypartStartTime ?? '--'}
                    </Text>
                  </VStack>

                  <VStack space={1} w="1/3">
                    <Text size="md" fontWeight={500} color="gray.600" numberOfLines={1} ellipsizeMode="tail" lineHeight="xs">
                      {t('store_management:min_staff')}
                    </Text>
                    <Text size="md" fontWeight={600} color="black" numberOfLines={1} ellipsizeMode="tail" lineHeight="xs">
                      {positionGroup?.positionMap?.minStaff ?? '--'}
                    </Text>
                  </VStack>
                </HStack>
              </VStack>
            </Box>
          </VStack>
        </ScrollView>
      </Box>

      <HStack space={3} alignItems="center" py={3} px={4} borderTopWidth={1}>
        <Button flex={1} size="sm" alternate outline onPress={props.onCancel}>
          {t('store_management:cancel')}
        </Button>

        <Button
          flex={1}
          size="sm"
          outline
          bg="primary.50"
          endIcon={<MaterialCommunityIcons name="share" size={20} color={colors.primary[600]} />}
          _hover={{ bg: 'primary.50' }}
          _pressed={{ bg: 'primary.50' }}
          _text={{ color: 'primary.600' }}
        >
          {t('store_management:share')}
        </Button>
      </HStack>
    </VStack>
  );
};
